/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/BarChart.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useMemo, useRef, useState } from 'react';
import { amt, chf, monthName } from '../lib/format';

/**
 * Grouped columns, two series (Ertrag / Aufwand) per month.
 * Spec (dataviz): ≤24px columns, 4px rounded data-end, square at the baseline, 2px surface gap,
 * recessive grid, legend (2 series), per-mark hover tooltip, table view, text never in series color.
 * Palette validated with validate_palette.js (light + dark, all checks pass).
 */
export function BarChart({ data }: { data: { m: string; rev: number; exp: number }[] }) {
  const [hover, setHover] = useState<{ i: number; s: 0 | 1 } | null>(null);
  const [table, setTable] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const W = 720, H = 240, pad = { l: 56, r: 8, t: 12, b: 28 };
  const max = Math.max(1, ...data.flatMap((d) => [d.rev, d.exp]));
  const nice = useMemo(() => { const p = 10 ** Math.floor(Math.log10(max)); const step = [1, 2, 2.5, 5, 10].map((k) => k * p).find((s) => max / s <= 4) ?? p * 10; return { step, top: Math.ceil(max / step) * step }; }, [max]);
  const band = (W - pad.l - pad.r) / Math.max(1, data.length);
  const bw = Math.min(24, (band - 16) / 2);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / nice.top);
  const base = y(0);
  const ticks = Array.from({ length: Math.round(nice.top / nice.step) + 1 }, (_, i) => i * nice.step);
  const bar = (x: number, v: number) => {
    const h = Math.max(0, base - y(v)); const r = Math.min(4, h);
    return `M${x},${base} L${x},${base - h + r} Q${x},${base - h} ${x + r},${base - h} L${x + bw - r},${base - h} Q${x + bw},${base - h} ${x + bw},${base - h + r} L${x + bw},${base} Z`;
  };
  const tip = hover ? data[hover.i] : null;
  const tipX = hover ? pad.l + band * hover.i + band / 2 : 0;

  return (
    <div className="stack">
      <div className="split" style={{ justifyContent: 'space-between' }}>
        <div className="legend"><span><i style={{ background: 'var(--series-1)' }} />Betriebsertrag</span><span><i style={{ background: 'var(--series-2)' }} />Aufwand</span></div>
        <div className="segmented" role="group" aria-label="Darstellung">
          <button aria-pressed={!table} onClick={() => setTable(false)}>Diagramm</button>
          <button aria-pressed={table} onClick={() => setTable(true)}>Tabelle</button>
        </div>
      </div>
      {table ? (
        <table className="data compact" data-copy-surface>
          <thead><tr><th>Monat</th><th className="r">Ertrag</th><th className="r">Aufwand</th><th className="r">Ergebnis</th></tr></thead>
          <tbody>{data.map((d) => <tr key={d.m}><td>{monthName(d.m)} {d.m.slice(0, 4)}</td><td className="r num">{amt(d.rev)}</td><td className="r num">{amt(d.exp)}</td><td className={`r num ${d.rev - d.exp < 0 ? 'neg' : ''}`}>{amt(d.rev - d.exp, false)}</td></tr>)}</tbody>
        </table>
      ) : (
        <div className="chart" ref={wrap} onMouseLeave={() => setHover(null)}>
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monatlicher Betriebsertrag und Aufwand">
            <g className="grid">{ticks.slice(1).map((t) => <line key={t} x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} />)}</g>
            <g className="axis">
              {ticks.map((t) => <text key={t} x={pad.l - 8} y={y(t) + 4} textAnchor="end">{t >= 1e5 ? `${Math.round(t / 1e5)}k` : amt(t)}</text>)}
              {data.map((d, i) => <text key={d.m} x={pad.l + band * i + band / 2} y={H - 8} textAnchor="middle">{monthName(d.m)}</text>)}
            </g>
            {data.map((d, i) => {
              const x0 = pad.l + band * i + (band - (bw * 2 + 2)) / 2;
              return (
                <g key={d.m}>
                  <rect x={pad.l + band * i} y={pad.t} width={band} height={H - pad.t - pad.b} fill="transparent" onMouseMove={() => setHover({ i, s: 0 })} />
                  <path className={`bar ${hover && hover.i !== i ? 'dim' : ''}`} d={bar(x0, d.rev)} fill="var(--series-1)" pointerEvents="none" />
                  <path className={`bar ${hover && hover.i !== i ? 'dim' : ''}`} d={bar(x0 + bw + 2, d.exp)} fill="var(--series-2)" pointerEvents="none" />
                </g>
              );
            })}
            <line className="baseline" x1={pad.l} x2={W - pad.r} y1={base} y2={base} />
          </svg>
          {tip && (
            <div className="tooltip" style={{ left: `${(tipX / W) * 100}%`, top: `${(y(Math.max(tip.rev, tip.exp)) / H) * 100}%` }}>
              <b>{monthName(tip.m)} {tip.m.slice(0, 4)}</b>
              <div className="split"><span><i className="dot" style={{ background: 'var(--series-1)', display: 'inline-block' }} /> Ertrag</span><span className="num" style={{ marginLeft: 'auto' }}>{chf(tip.rev)}</span></div>
              <div className="split"><span><i className="dot" style={{ background: 'var(--series-2)', display: 'inline-block' }} /> Aufwand</span><span className="num" style={{ marginLeft: 'auto' }}>{chf(tip.exp)}</span></div>
              <div className="split muted"><span>Ergebnis</span><span className="num" style={{ marginLeft: 'auto' }}>{chf(tip.rev - tip.exp)}</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
